// slices/studyHistory/studyHistorySlice.ts
/**
 * Redux slice that stores study-history data for
 *   • **all** of a user’s exams combined
 *   • the user’s **primaryExam** only
 *
 * Both buckets share the exact same inner structure:
 *   {
 *     data:   Record<YYYY-MM-DD, { qbank, flashcards }>,
 *     status: "idle" | "loading" | "succeeded" | "failed",
 *     error:  string | null
 *   }
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { logout } from '../auth/authSlice';

/* ── Types ────────────────────────────────────────────── */
interface QBankData {
  correct: number;
  total: number;
  score: number;
}

interface FlashcardsData {
  total: number;
}

interface VideoData {
  started: number;
  total: number;
}

export interface StudyHistoryData {
  qbank: QBankData;
  flashcards: FlashcardsData;
  videos: VideoData;
}

export type StudyHistoryRecord = Record<string, StudyHistoryData>;
type StudyHistoryStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

/**
 * One “bucket” (all | primaryExam) in the slice
 */
interface ScopeState {
  data: StudyHistoryRecord;
  status: StudyHistoryStatus;
  error: string | null;
}

type StudyScope = 'all' | 'primaryExam';

interface StudyHistorySliceState {
  all: ScopeState;
  primaryExam: ScopeState;
}

/* ── Helpers ──────────────────────────────────────────── */
const emptyScope: ScopeState = { data: {}, status: 'idle', error: null };

const initialState: StudyHistorySliceState = {
  all: { ...emptyScope },
  primaryExam: { ...emptyScope },
};

const getScopeState = (
  state: StudyHistorySliceState,
  scope: StudyScope
): ScopeState => (scope === 'all' ? state.all : state.primaryExam);

/* ── Slice ────────────────────────────────────────────── */
const studyHistorySlice = createSlice({
  name: 'studyHistory',
  initialState,
  reducers: {
    /* Set loading */
    studyHistoryLoading: (
      state,
      { payload: scope }: PayloadAction<StudyScope>
    ) => {
      const s = getScopeState(state, scope);
      s.status = 'loading';
      s.error = null;
    },

    /* Replace entire record */
    studyHistoryReceived: (
      state,
      {
        payload,
      }: PayloadAction<{ scope: StudyScope; data: StudyHistoryRecord }>
    ) => {
      const s = getScopeState(state, payload.scope);
      s.data = payload.data;
      s.status = 'succeeded';
      s.error = null;
    },

    /* Upsert a single date */
    studyHistoryForDateReceived: (
      state,
      {
        payload,
      }: PayloadAction<{
        scope: StudyScope;
        date: string;
        data: StudyHistoryData;
      }>
    ) => {
      const s = getScopeState(state, payload.scope);
      s.data[payload.date] = payload.data;
      s.status = 'succeeded';
      s.error = null;
    },

    /* Error */
    studyHistoryFailed: (
      state,
      { payload }: PayloadAction<{ scope: StudyScope; error?: string | null }>
    ) => {
      const s = getScopeState(state, payload.scope);
      s.status = 'failed';
      s.error = payload.error ?? 'Failed to load study history';
    },

    /* Clear (per-scope or both) */
    clearStudyHistory: (
      state,
      { payload }: PayloadAction<StudyScope | 'allScopes'>
    ) => {
      if (payload === 'all' || payload === 'allScopes')
        state.all = { ...emptyScope };
      if (payload === 'primaryExam' || payload === 'allScopes')
        state.primaryExam = { ...emptyScope };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      state.all = { ...emptyScope };
      state.primaryExam = { ...emptyScope };
    });
  },
});

/* ── Actions ─────────────────────────────────────────── */
export const { studyHistoryLoading, studyHistoryReceived, studyHistoryFailed } =
  studyHistorySlice.actions;

/* ── Reducer export ──────────────────────────────────── */
export default studyHistorySlice.reducer;
